import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Rocket, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

type EnvName = "production" | "staging" | "preview" | "development";

interface ProjectPayload {
  project: {
    envChanges?: Record<string, string>;
    stagingBranch?: string | null;
    developmentBranch?: string | null;
    [k: string]: unknown;
  };
}
interface Deployment {
  id: string;
  env: EnvName;
  status: string;
  createdAt: string;
}

/**
 * Banner shown above each project's tabs when env vars have changed since
 * the most recent successful deployment for a given env. One-click redeploy
 * per affected env — env changes don't auto-redeploy per spec FR-068.
 */
export function PendingEnvBanner({ orgSlug, projectSlug }: { orgSlug: string; projectSlug: string }) {
  const qc = useQueryClient();
  const nav = useNavigate();

  const project = useQuery({
    queryKey: ["project", orgSlug, projectSlug],
    queryFn: () => api.get<ProjectPayload>(`/orgs/${orgSlug}/projects/${projectSlug}`).then((r) => r.project),
  });
  const deps = useQuery({
    queryKey: ["deployments", orgSlug, projectSlug],
    queryFn: () =>
      api
        .get<{ deployments: Deployment[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/deployments?limit=50`)
        .then((r) => r.deployments),
  });

  const redeploy = useMutation({
    mutationFn: (env: EnvName) =>
      api.post<{ deploymentId: string }>(`/orgs/${orgSlug}/projects/${projectSlug}/redeploy`, { env }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["deployments", orgSlug, projectSlug] });
      nav(`/orgs/${orgSlug}/projects/${projectSlug}/deployments/${r.deploymentId}`);
    },
  });

  const envChanges = project.data?.envChanges ?? {};
  // Preview has no dedicated branch setting (it's the catch-all for any
  // branch that doesn't match production/staging/development) and the
  // redeploy endpoint doesn't accept it, so it can never be redeployed from
  // here. Staging/development only get a real branch when the project
  // configures one — without it there's nothing to redeploy either, so
  // don't nag about env vars pending for an environment the project isn't
  // even using.
  const stale = Object.entries(envChanges).filter(([env, ts]) => {
    if (env === "preview") return false;
    if (env === "staging" && !project.data?.stagingBranch) return false;
    if (env === "development" && !project.data?.developmentBranch) return false;
    if (!ts) return false;
    const envChangeMs = new Date(ts as string).getTime();
    if (Number.isNaN(envChangeMs)) return false;
    const lastReady = (deps.data ?? []).find((d) => d.env === env && d.status === "ready");
    if (!lastReady) return true; // never deployed this env → always stale if vars exist
    return new Date(lastReady.createdAt).getTime() < envChangeMs;
  }) as [EnvName, string][];

  if (stale.length === 0) return null;
  return (
    <div className="mb-4 of-card border-attention/40 bg-attention-subtle/60">
      <div className="px-4 py-3 flex items-start gap-3 flex-wrap">
        <AlertTriangle className="h-4 w-4 text-attention shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">Env variables changed</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Pending in {stale.map(([e]) => <span key={e} className="font-mono">{e}</span>).reduce((acc, x, i) => i === 0 ? [x] : [...acc, ", ", x], [] as React.ReactNode[])}.
            Redeploy to apply.
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {stale.map(([env]) => (
            <Button
              key={env}
              variant="primary"
              size="sm"
              disabled={redeploy.isPending}
              onClick={() => redeploy.mutate(env)}
            >
              {redeploy.isPending && redeploy.variables === env ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Rocket className="h-3.5 w-3.5" />
              )}
              Redeploy {env}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
